import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs";
import * as moment from "moment";
import {BaseComponent} from "../base/base.component";
import {CategoryService} from "../../services/category/category.service";
import {SeasonService} from "../../services/season/season.service";
import {AthleteService} from "../../services/athlete/athlete.service";
import {Category} from "../../models/category";
import {Season} from "../../models/season";
import {AthleteSeason} from "../../models/athleteSeason";
import {computeEnrollmentStats, EnrollmentStats, formatEuro, percent} from "./enrollment";

interface CategoryOverview {
    category: Category;
    stats: EnrollmentStats;
}

interface CategoryGroupOverview {
    parent: Category;
    stats: EnrollmentStats;
    children: CategoryOverview[];
}

@Component({
    selector: 'app-companies-subscriptions',
    templateUrl: './companies-subscriptions.component.html',
    styleUrls: ['./companies-subscriptions.component.scss']
})
export class CompaniesSubscriptionsComponent extends BaseComponent implements OnInit, OnDestroy {

    companyId = localStorage.getItem('wisper_companyId') ?? '';

    loading = true;
    loadError = false;
    season?: Season;
    seasons: Season[] = [];
    stats: EnrollmentStats = computeEnrollmentStats([]);
    groups: CategoryGroupOverview[] = [];

    search = '';
    hideEmpty = false;

    readonly skeletonGroups = [[1, 2, 3], [1, 2]];
    readonly formatEuro = formatEuro;
    readonly percent = percent;

    constructor(
        public injectorService: Injector,
        private categoryService: CategoryService,
        private seasonService: SeasonService,
        private athleteService: AthleteService,
        private route: ActivatedRoute,
    ) {
        super(injectorService);
    }

    private querySub?: Subscription;

    ngOnInit() {
        // `?seasonId=` selects a past/future season; without it the current season is shown.
        this.querySub = this.route.queryParamMap.subscribe(params => this.load(params.get('seasonId') ?? undefined));
    }

    ngOnDestroy() {
        this.querySub?.unsubscribe();
    }

    async load(seasonId?: string) {
        this.loading = true;
        this.loadError = false;

        if (!this.seasons.length) {
            const seasonsResponse = await this.seasonService.getSeasons(this.companyId, {$orderby: 'startDate desc'});
            if (!seasonsResponse?.success) {
                this.loadError = true;
                this.loading = false;
                return;
            }
            this.seasons = Season.fromApiList(seasonsResponse.data);
        }
        this.season = this.seasons.find(season => season.id === seasonId) ?? this.seasons.find(season => !!season.isCurrent);
        if (!this.season) {
            this.loading = false;
            return;
        }

        const requestedId = this.season.id!;
        const [categories, enrollments] = await Promise.all([
            this.categoryService.getCategories(this.companyId, false, {seasonId: requestedId}),
            this.athleteService.getEnrollments({seasonId: requestedId}),
        ]);
        // Ignore a stale response if the user switched season meanwhile.
        if (requestedId !== this.season?.id) return;
        if (!enrollments) this.loadError = true;

        this.buildOverview(categories.map(item => new Category(item)), enrollments ?? []);
        this.loading = false;
    }

    selectSeason(seasonId: string) {
        const isCurrent = !seasonId || this.seasons.find(season => season.id === seasonId)?.isCurrent;
        void this.navigate.navigate([], {
            relativeTo: this.route,
            queryParams: {seasonId: isCurrent ? null : seasonId},
            replaceUrl: true,
        });
    }

    seasonPhaseLabel(season: Season): string {
        if (season.isCurrent) return 'In corso';
        if (season.startDate && moment(season.startDate).isAfter(moment())) return 'Futura';
        return 'Passata';
    }

    get isCurrentSeason(): boolean {
        return !!this.season?.isCurrent;
    }

    get visibleGroups(): CategoryGroupOverview[] {
        const query = this.search.trim().toLowerCase();
        return this.groups
            .map(group => {
                const parentMatches = !query || group.parent.name.toLowerCase().includes(query);
                const children = group.children.filter(child =>
                    (!this.hideEmpty || child.stats.total > 0) &&
                    (parentMatches || child.category.name.toLowerCase().includes(query)));
                return {...group, children};
            })
            .filter(group => group.children.length);
    }

    get hasCategories(): boolean {
        return this.groups.some(group => group.children.length);
    }

    barWidth(part: number, total: number): string {
        return `${total ? part / total * 100 : 0}%`;
    }

    trackByCategory(_: number, item: CategoryOverview | CategoryGroupOverview) {
        return 'parent' in item ? item.parent.id : item.category.id;
    }

    private buildOverview(categories: Category[], enrollments: AthleteSeason[]) {
        this.stats = computeEnrollmentStats(enrollments);

        const byCategory = new Map<string, AthleteSeason[]>();
        for (const enrollment of enrollments) {
            if (!enrollment.categoryId) continue;
            byCategory.set(enrollment.categoryId, [...(byCategory.get(enrollment.categoryId) ?? []), enrollment]);
        }

        const overview = (category: Category): CategoryOverview => ({
            category,
            stats: computeEnrollmentStats(byCategory.get(category.id!) ?? []),
        });

        const parents = categories.filter(category => !category.parentId);
        this.groups = parents
            .map(parent => {
                let children = categories
                    .filter(category => category.parentId === parent.id)
                    .map(overview)
                    .sort((a, b) => a.category.name.localeCompare(b.category.name, 'it', {numeric: true}));
                // A category without subcategories can still hold enrollments: show it as its own card.
                if (!children.length && byCategory.has(parent.id!)) children = [overview(parent)];
                const groupEnrollments = children.flatMap(child => byCategory.get(child.category.id!) ?? []);
                return {parent, children, stats: computeEnrollmentStats(groupEnrollments)};
            })
            .filter(group => group.children.length);
    }
}
